import React from 'react';
import { toast } from 'react-toastify';
import { Download } from 'lucide-react';
import useWorkflowStore from '../../stores/useWorkflowStore';
import useEditorStore from '../../stores/useEditorStore';
import { downloadMesh } from '../../lib/exporters/modelExporter';
import { generateFileName } from '../../lib/exportConfig';
import { getEffectiveExportFormat } from '../../lib/exportFormatPolicy';
import { EXPORT_FORMATS } from '../../lib/exportConfig';
import { downloadOBJUploadedPackage } from '../../lib/exporters/objExporter';
import { cn } from '../../lib/utils';
import { isMeshDownloadAllowed } from '../../lib/meshDownloadAccess';

// Internal affordance: the mesh download button always ships in `npm run dev`.
// In deployed builds it renders only on hostnames listed in the
// `VITE_MESH_DOWNLOAD_HOSTS` allowlist (see .env.example).
const StepSceneControls = ({ visible }) => {
  const currentStep = useWorkflowStore((state) => state.currentStep);
  const shadedMesh = useEditorStore((state) => state.sceneRefs?.shadedMesh);
  const uploadedModelData = useEditorStore((state) => state.uploadedModelData);
  const selectedModelPath = useEditorStore((state) => state.selectedModelPath);

  if (!isMeshDownloadAllowed()) return null;
  if (!visible) return null;

  const handleDownloadMesh = () => {
    if (!shadedMesh?.geometry) {
      toast.error('No mesh loaded to download.');
      return;
    }
    try {
      const format = getEffectiveExportFormat(currentStep, uploadedModelData, selectedModelPath);
      if (format === EXPORT_FORMATS.OBJ && uploadedModelData?.type === 'obj' && uploadedModelData?.data?.objUrl) {
        downloadOBJUploadedPackage(uploadedModelData.data);
        toast.success('Mesh downloaded.');
        return;
      }
      downloadMesh(shadedMesh, {
        format,
        filename: generateFileName('socket', format),
      });
      toast.success('Mesh downloaded.');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download mesh.');
    }
  };

  return (
    <button
      type="button"
      className={cn(
        'pointer-events-auto flex h-10 w-10 items-center justify-center rounded-md border border-black/15 bg-[#fafafa]/50 text-[var(--text-primary)] transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--focus-ring,#0070f3)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-45'
      )}
      onClick={handleDownloadMesh}
      disabled={!shadedMesh?.geometry}
      title={shadedMesh?.geometry ? 'Download current mesh' : 'No mesh loaded'}
      aria-label="Download mesh"
    >
      <Download size={18} strokeWidth={2} aria-hidden />
    </button>
  );
};

export default StepSceneControls;
